import { jsx as _jsx } from "react/jsx-runtime";
/**
 * org-admin i18n runtime — a tiny, dependency-free translation layer.
 *
 * Design:
 *  - `OrgAdminI18nProvider` publishes the active locale's dictionary via React
 *    context. The shared `OrgAdminLayout` wraps its whole subtree in it.
 *  - `useOrgAdminT()` returns `t(key, vars?)` — a dot-path lookup into the
 *    active dictionary, with **en-US fallback** for any key the active locale
 *    is missing, then the key path itself as a last resort (lets callers do a
 *    `label === key ? raw : label` guard for open-ended enum values). `{var}`
 *    placeholders are interpolated from `vars`.
 *  - The context DEFAULTS to en-US, so a consumer that renders org-admin
 *    components WITHOUT a provider (LexPortal) keeps rendering English with no
 *    code change. That default is what guarantees LexPortal is byte-unchanged.
 */
import { createContext, useContext, useMemo } from "react";
import { enUS } from "./en-US.js";
import { ptBR } from "./pt-BR.js";
import { esES } from "./es-ES.js";
const DICTS = {
    "en-US": enUS,
    "pt-BR": ptBR,
    "es-ES": esES,
};
/** Dot-path lookup into a nested dictionary. Returns the string at `path`, or
 *  `undefined` if any segment is missing / the leaf isn't a string. */
function lookup(dict, path) {
    let cur = dict;
    for (const seg of path.split(".")) {
        if (cur === null || typeof cur !== "object")
            return undefined;
        cur = cur[seg];
    }
    return typeof cur === "string" ? cur : undefined;
}
/** `{var}` interpolation — leaves unknown placeholders untouched. */
function interpolate(template, vars) {
    if (!vars)
        return template;
    return template.replace(/\{(\w+)\}/g, (m, name) => name in vars ? String(vars[name]) : m);
}
const OrgAdminI18nContext = createContext("en-US");
export function OrgAdminI18nProvider({ locale, children, }) {
    return _jsx(OrgAdminI18nContext.Provider, { value: locale, children: children });
}
export function useOrgAdminT() {
    const locale = useContext(OrgAdminI18nContext);
    return useMemo(() => {
        const active = DICTS[locale] ?? enUS;
        return (key, vars) => {
            const raw = lookup(active, key) ?? lookup(enUS, key) ?? key;
            return interpolate(raw, vars);
        };
    }, [locale]);
}
//# sourceMappingURL=context.js.map